import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaLicenses } from '../../../hooks/useAdminSplaCatalog';
import { DECIMAL_RE, trimDecimalZeros } from '../../../utils/money';
import { SPLA_SUBJECT_TYPE_LABELS } from '../../utils/splaSubjects';
import type {
  SplaBinding,
  SplaBindingCreateRequest,
  SplaBindingRule,
  SplaBindingUpdateRequest,
  SplaSubjectType,
} from '../../../types/models/Spla';
import type { ApiResponseSplaBinding } from '../../../types/api/ApiResponseSpla';
import type { ApiResponseClientList } from '../../../types/api/ApiResponseClient';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';

const SUBJECT_LABEL_MAX = 160;
const NOTE_MAX = 255;

export const SPLA_RULE_LABELS: Record<SplaBindingRule, string> = {
  host_cores_half: '½ host cores',
  own_cores_half: '½ own cores',
  fixed: 'fixed',
};

const RULE_DESCRIPTIONS: Record<SplaBindingRule, string> = {
  host_cores_half:
    'A virtual machine licensed on its HOST: live NetBox resolves the host, the registry supplies its cores → ceil(cores / 2) two-core packs. A migrated VM follows its new host automatically.',
  own_cores_half:
    'Licensed on the subject’s OWN cores → ceil(cores / 2). A server uses the registry; a virtual machine uses its own core_count custom field (the SQL Server per-virtual-core pattern).',
  fixed: 'The fixed quantity below, verbatim — for hand-maintained counts such as per-user SALs.',
};

/** Rule ↔ subject coherence, mirrored client-side (the API re-validates). */
export function splaRuleAllowsSubject(rule: SplaBindingRule, type: SplaSubjectType): boolean {
  switch (rule) {
    case 'host_cores_half':
      return type === 'virtual_machine';
    case 'own_cores_half':
      return type === 'virtual_machine' || type === 'device';
    default:
      return true;
  }
}

// Backend field-error keys rendered inline on the matching input; anything else (body, id, …)
// lands in the form-level alert.
const FIELD_ERROR_KEYS = new Set([
  'clientID',
  'spla_license_id',
  'subject_type',
  'subject_ref',
  'subject_label',
  'rule',
  'fixed_quantity',
  'active',
  'note',
]);

/** Candidate-queue prefill: the subject is locked, the client is a SUGGESTION (fully editable). */
export interface SplaBindingPrefill {
  subjectType: SplaSubjectType;
  subjectRef: string;
  subjectLabel: string | null;
  suggestedClientId: number | null;
  suggestedClientName: string | null;
}

interface SplaBindingModalProps {
  isOpen: boolean;
  /** `null` = create; a binding = edit (parent keys the mount so state is always fresh). */
  binding: SplaBinding | null;
  /** Create-mode prefill from the candidates queue (subject locked, client suggested). */
  prefill?: SplaBindingPrefill | null;
  onClose: () => void;
  /** Hand the saved row back: `isCreate` tells the tab to refetch vs patch in place. */
  onSaved: (binding: SplaBinding, isCreate: boolean) => void;
}

/**
 * Create or edit one binding — the license ↔ subject attachment that auto-maintains a `computed`
 * consumption row by rule.
 *
 * The client is an EXPLICIT STAFF DECISION: attribution is never derived from the NetBox owner
 * (shared-hypervisor VMs make the owner wrong exactly here). When opened from the candidates queue
 * the suggestion pre-selects the client, but it is fully editable and clearly marked as a hint.
 *
 * Identity (client, license, subject) is immutable after create — move = delete + create. The
 * create computes once: the response's `computed_quantity` (success) or `compute_error` (the
 * binding was still created — fix the cause, then recompute) drives the toast.
 */
function SplaBindingModal({ isOpen, binding, prefill = null, onClose, onSaved }: SplaBindingModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const isEdit = binding !== null;
  const subjectLocked = prefill !== null;

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const licenseFilters = useMemo(() => ({ agreementType: null, archived: '0' as const }), []);
  const {
    licenses,
    loading: licensesLoading,
    error: licensesError,
  } = useAdminSplaLicenses(isOpen && !isEdit, licenseFilters);

  const [clientId, setClientId] = useState<number | null>(
    binding?.client.id ?? prefill?.suggestedClientId ?? null,
  );
  const [licenseId, setLicenseId] = useState<string>(binding !== null ? String(binding.license.id) : '');
  const [subjectType, setSubjectType] = useState<SplaSubjectType>(
    binding?.subject.type ?? prefill?.subjectType ?? 'virtual_machine',
  );
  const [subjectRef, setSubjectRef] = useState(binding?.subject.ref ?? prefill?.subjectRef ?? '');
  const [subjectLabel, setSubjectLabel] = useState(binding?.subject.label ?? prefill?.subjectLabel ?? '');
  const [rule, setRule] = useState<SplaBindingRule | ''>(binding?.rule ?? '');
  const [fixedQuantity, setFixedQuantity] = useState(binding?.fixed_quantity ?? '');
  const [active, setActive] = useState(binding?.active ?? true);
  const [note, setNote] = useState(binding?.note ?? '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // `apiFetch` gets a NEW identity on every OIDC silent renew — fetch the client list exactly once
  // per mount so a renew never re-runs it mid-edit.
  const hasFetchedClients = useRef(false);

  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    setClientsError(null);
    try {
      const response = await apiFetch('/clients');
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseClientList | null;
      if (body?.status === 'success' && body.data?.clients) {
        setClients(body.data.clients.map((client) => ({ id: client.id, name: client.name })));
      } else {
        setClientsError(body?.message ?? 'The client list could not be loaded.');
      }
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'The client list could not be loaded.');
    } finally {
      setClientsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (isEdit || hasFetchedClients.current) {
      return;
    }
    hasFetchedClients.current = true;
    void fetchClients();
  }, [isEdit, fetchClients]);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const trimmedRef = subjectRef.trim();
  const trimmedLabel = subjectLabel.trim();
  const trimmedFixed = fixedQuantity.trim();
  const trimmedNote = note.trim();

  const effectiveSubjectType = binding?.subject.type ?? subjectType;
  const isRefValid = effectiveSubjectType === 'manual' ? trimmedRef.length > 0 : /^\d+$/.test(trimmedRef);
  const isLabelValid = trimmedLabel.length <= SUBJECT_LABEL_MAX;
  const isRuleCoherent = rule === '' || splaRuleAllowsSubject(rule, effectiveSubjectType);
  const isFixedValid =
    rule !== 'fixed' || (DECIMAL_RE.test(trimmedFixed) && !/^0+(\.0+)?$/.test(trimmedFixed));
  const isNoteValid = trimmedNote.length <= NOTE_MAX;

  const buildUpdateBody = (original: SplaBinding): SplaBindingUpdateRequest => {
    const body: SplaBindingUpdateRequest = {};
    if (rule !== '' && rule !== original.rule) {
      body.rule = rule;
    }
    if (rule === 'fixed') {
      if (trimmedFixed !== (original.fixed_quantity ?? '')) {
        body.fixed_quantity = trimmedFixed;
      }
    } else if (rule !== '' && original.fixed_quantity !== null) {
      // Leaving 'fixed' must clear the stored quantity IN THE SAME change-set — the backend
      // re-validates the merged row and rejects a non-fixed rule with a lingering fixed_quantity.
      body.fixed_quantity = null;
    }
    if (active !== original.active) {
      body.active = active;
    }
    if (trimmedLabel !== (original.subject.label ?? '')) {
      body.subject_label = trimmedLabel === '' ? null : trimmedLabel;
    }
    if (trimmedNote !== (original.note ?? '')) {
      body.note = trimmedNote === '' ? null : trimmedNote;
    }
    return body;
  };

  const hasChanges = binding === null || Object.keys(buildUpdateBody(binding)).length > 0;
  const isFormValid = isEdit
    ? rule !== '' && isRuleCoherent && isFixedValid && isLabelValid && isNoteValid
    : clientId !== null &&
      licenseId !== '' &&
      isRefValid &&
      rule !== '' &&
      isRuleCoherent &&
      isFixedValid &&
      isLabelValid &&
      isNoteValid;
  const canSubmit = !isSaving && isFormValid && hasChanges;

  const buildCreateBody = (): SplaBindingCreateRequest => {
    const body: SplaBindingCreateRequest = {
      clientID: clientId as number,
      spla_license_id: Number(licenseId),
      subject_type: subjectType,
      subject_ref: subjectType === 'manual' ? trimmedRef : Number(trimmedRef),
      rule: rule as SplaBindingRule,
    };
    if (rule === 'fixed') {
      body.fixed_quantity = trimmedFixed;
    }
    if (trimmedLabel !== '') {
      body.subject_label = trimmedLabel;
    }
    if (trimmedNote !== '') {
      body.note = trimmedNote;
    }
    return body;
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(binding !== null ? `/admin/spla/bindings/${binding.id}` : '/admin/spla/bindings', {
        method: binding !== null ? 'PATCH' : 'POST',
        body: JSON.stringify(binding !== null ? buildUpdateBody(binding) : buildCreateBody()),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaBinding | null;
      if (body?.status === 'success' && body.data?.binding) {
        const saved = body.data.binding;
        onSaved(saved, !isEdit);
        const computeError = body.data.compute_error ?? null;
        if (computeError !== null) {
          // The binding exists — the compute just failed. Fix the cause (e.g. register the host's
          // cores), then recompute; the previous consumption row (if any) is kept meanwhile.
          addNotification({
            title: isEdit ? 'Binding saved, compute failed' : 'Binding created, compute failed',
            srTitle: 'Warning alert',
            description: `${computeError} — fix the cause, then recompute.`,
            variant: 'warning',
          });
        } else {
          const computed = body.data.computed_quantity ?? null;
          addNotification({
            title: isEdit ? 'Binding updated' : 'Binding created',
            srTitle: 'Success alert',
            description:
              computed !== null
                ? `Computed quantity: ${trimDecimalZeros(computed)} × ${saved.license.part_number ?? `license #${saved.license.id}`}.`
                : `${saved.license.part_number ?? `License #${saved.license.id}`} bound by "${SPLA_RULE_LABELS[saved.rule]}".`,
            variant: 'success',
          });
        }
        setDrawerOpen(true);
        onClose();
        return;
      }

      // Split the field-keyed validation map: known keys inline (the duplicate-binding refusal
      // arrives under subject_ref), the rest — incl. the pseudo-field `body` — in the form alert.
      const errors: Record<string, string> = {};
      const generalParts: string[] = [];
      if (body?.data) {
        for (const [key, value] of Object.entries(body.data)) {
          if (typeof value !== 'string') {
            continue;
          }
          if (key === 'compute_error') {
            continue;
          }
          if (FIELD_ERROR_KEYS.has(key)) {
            errors[key] = value;
          } else {
            generalParts.push(value);
          }
        }
      }
      const general =
        generalParts.length > 0
          ? generalParts.join(' ')
          : Object.keys(errors).length === 0
            ? (body?.message ?? 'Could not save the binding. Please try again.')
            : null;
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update the binding' : 'Could not create the binding',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update the binding' : 'Could not create the binding',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldHelper = (key: string, fallback?: string) =>
    (fieldErrors[key] !== undefined || fallback !== undefined) && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant={fieldErrors[key] !== undefined ? 'error' : 'default'}>
            {fieldErrors[key] ?? fallback}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    );

  const suggestedName = prefill?.suggestedClientName ?? null;
  const suggestionStillSelected = prefill !== null && clientId !== null && clientId === prefill.suggestedClientId;

  const ruleOptions = (['host_cores_half', 'own_cores_half', 'fixed'] as SplaBindingRule[]).map((r) => (
    <FormSelectOption
      key={r}
      value={r}
      label={SPLA_RULE_LABELS[r]}
      isDisabled={!splaRuleAllowsSubject(r, effectiveSubjectType)}
    />
  ));

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={isEdit ? 'Edit binding' : 'Create binding'}
    >
      <ModalHeader
        title={isEdit ? 'Edit binding' : 'Create binding'}
        description="A binding attaches a license to a subject by a rule; the computed quantity lands in Consumption automatically."
      />
      <ModalBody>
        <Form
          id="spla-binding-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Creation failed'}>
              {formError}
            </Alert>
          )}

          {isEdit ? (
            <>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>Client</DescriptionListTerm>
                  <DescriptionListDescription>
                    {binding.client.name ?? `Client #${binding.client.id}`}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>License</DescriptionListTerm>
                  <DescriptionListDescription>
                    {binding.license.part_number ?? `#${binding.license.id}`} —{' '}
                    {binding.license.product_family ?? ''}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Subject</DescriptionListTerm>
                  <DescriptionListDescription>
                    {SPLA_SUBJECT_TYPE_LABELS[binding.subject.type]} · {binding.subject.ref}
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Identity is immutable — moving a binding = delete + create (its computed consumption row
                    follows the same key).
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </>
          ) : (
            <>
              <FormGroup label="Client" isRequired fieldId="spla-binding-client-input">
                {clientsError === null ? (
                  <ClientTypeahead
                    clients={clients}
                    value={clientId}
                    onChange={(id) => {
                      setClientId(id);
                      clearFieldError('clientID');
                    }}
                    isDisabled={isSaving || clientsLoading}
                    idPrefix="spla-binding-client"
                  />
                ) : (
                  <Alert variant="warning" isInline title="The client list is unavailable">
                    {clientsError}
                    {clientId !== null && suggestedName !== null ? (
                      <>
                        {' '}
                        You can still save the suggested client <strong>{suggestedName}</strong>.
                      </>
                    ) : (
                      ' End the impersonation session (or reopen this dialog) to pick a client.'
                    )}
                  </Alert>
                )}
                {fieldErrors['clientID'] !== undefined ? (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{fieldErrors['clientID']}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                ) : (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant={suggestionStillSelected ? 'warning' : 'default'}>
                        {suggestionStillSelected
                          ? `Pre-selected from the NetBox owner suggestion (${suggestedName ?? '?'}) — often wrong for VMs on shared hypervisors. YOU pick the client.`
                          : 'An explicit staff decision — attribution is never derived from the NetBox owner.'}
                      </HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>

              <FormGroup label="License" isRequired fieldId="spla-binding-license">
                {licensesError !== null && (
                  <Alert variant="warning" isInline title="Could not load the license catalog">
                    {licensesError}
                  </Alert>
                )}
                <FormSelect
                  id="spla-binding-license"
                  value={licenseId}
                  onChange={(_event, value) => {
                    setLicenseId(value);
                    clearFieldError('spla_license_id');
                  }}
                  aria-label="Bound license"
                  isDisabled={isSaving || licensesLoading}
                >
                  <FormSelectOption
                    value=""
                    label={licensesLoading ? 'Loading licenses…' : 'Select a license'}
                    isDisabled
                  />
                  {licenses.map((license) => (
                    <FormSelectOption
                      key={license.id}
                      value={String(license.id)}
                      label={`${license.part_number} — ${license.product_family} (${license.agreement_type}${license.agreement_type === 'Academic' ? ` · ${license.offering}` : ''})`}
                    />
                  ))}
                </FormSelect>
                {fieldHelper('spla_license_id', 'Retired licenses gain no new bindings and are not offered.')}
              </FormGroup>

              <FormGroup label="Subject type" fieldId="spla-binding-subject-type">
                <FormSelect
                  id="spla-binding-subject-type"
                  value={subjectType}
                  onChange={(_event, value) => {
                    const next = value as SplaSubjectType;
                    setSubjectType(next);
                    // Keep the rule coherent with the new subject type (the API re-validates).
                    if (rule !== '' && !splaRuleAllowsSubject(rule, next)) {
                      setRule('');
                    }
                    clearFieldError('subject_type');
                    clearFieldError('subject_ref');
                  }}
                  aria-label="Subject type"
                  isDisabled={isSaving || subjectLocked}
                >
                  <FormSelectOption value="virtual_machine" label="Virtual machine" />
                  <FormSelectOption value="device" label="Server (device)" />
                  <FormSelectOption value="manual" label="Manual" />
                </FormSelect>
                {fieldHelper('subject_type', subjectLocked ? 'Locked from the discovery candidate.' : undefined)}
              </FormGroup>

              <FormGroup
                label={subjectType === 'manual' ? 'Subject name' : 'NetBox id'}
                isRequired
                fieldId="spla-binding-subject-ref"
              >
                <TextInput
                  id="spla-binding-subject-ref"
                  type="text"
                  inputMode={subjectType === 'manual' ? 'text' : 'numeric'}
                  value={subjectRef}
                  placeholder={subjectType === 'manual' ? 'sql-instance-erp' : '4242'}
                  onChange={(_event, value) => {
                    setSubjectRef(value);
                    clearFieldError('subject_ref');
                  }}
                  validated={
                    fieldErrors['subject_ref'] !== undefined || (trimmedRef !== '' && !isRefValid)
                      ? 'error'
                      : 'default'
                  }
                  isDisabled={isSaving || subjectLocked}
                />
                {fieldHelper(
                  'subject_ref',
                  subjectLocked
                    ? 'Locked from the discovery candidate.'
                    : trimmedRef !== '' && !isRefValid
                      ? 'Enter the numeric NetBox id.'
                      : subjectType === 'manual'
                        ? 'A stable name for the consumer — it is the upsert identity.'
                        : 'The numeric NetBox id of the subject.',
                )}
              </FormGroup>
            </>
          )}

          <FormGroup label="Subject label" fieldId="spla-binding-subject-label">
            <TextInput
              id="spla-binding-subject-label"
              type="text"
              value={subjectLabel}
              placeholder="broker.ancalagon.be"
              onChange={(_event, value) => {
                setSubjectLabel(value);
                clearFieldError('subject_label');
              }}
              validated={fieldErrors['subject_label'] !== undefined || !isLabelValid ? 'error' : 'default'}
              isDisabled={isSaving || subjectLocked}
            />
            {fieldHelper(
              'subject_label',
              subjectLocked
                ? 'Locked from the discovery candidate.'
                : !isLabelValid
                  ? `At most ${SUBJECT_LABEL_MAX} characters.`
                  : 'Optional display name; the ref is shown when empty.',
            )}
          </FormGroup>

          <FormGroup label="Rule" isRequired fieldId="spla-binding-rule">
            <FormSelect
              id="spla-binding-rule"
              value={rule}
              onChange={(_event, value) => {
                setRule(value as SplaBindingRule | '');
                clearFieldError('rule');
                clearFieldError('fixed_quantity');
              }}
              aria-label="Quantity rule"
              isDisabled={isSaving}
            >
              <FormSelectOption value="" label="Select a rule" isDisabled />
              {ruleOptions}
            </FormSelect>
            {fieldHelper(
              'rule',
              rule !== ''
                ? RULE_DESCRIPTIONS[rule]
                : '½ host cores needs a virtual machine; ½ own cores a server or a virtual machine; fixed accepts any subject.',
            )}
          </FormGroup>

          {rule === 'fixed' && (
            <FormGroup label="Fixed quantity" isRequired fieldId="spla-binding-fixed-quantity">
              <TextInput
                id="spla-binding-fixed-quantity"
                type="text"
                inputMode="decimal"
                value={fixedQuantity}
                placeholder="4"
                onChange={(_event, value) => {
                  setFixedQuantity(value);
                  clearFieldError('fixed_quantity');
                }}
                validated={
                  fieldErrors['fixed_quantity'] !== undefined || (trimmedFixed !== '' && !isFixedValid)
                    ? 'error'
                    : 'default'
                }
                isDisabled={isSaving}
                aria-label="Fixed quantity of units per month"
              />
              {fieldHelper(
                'fixed_quantity',
                trimmedFixed !== '' && !isFixedValid
                  ? 'Enter a strictly positive amount with up to 4 decimal places.'
                  : 'Written to consumption verbatim on every recompute.',
              )}
            </FormGroup>
          )}

          {isEdit && (
            <FormGroup label="Active" fieldId="spla-binding-active">
              <Switch
                id="spla-binding-active"
                aria-label="Toggle whether this binding recomputes"
                isChecked={active}
                onChange={(_event, checked) => {
                  setActive(checked);
                  clearFieldError('active');
                }}
                isDisabled={isSaving}
              />
              {fieldHelper(
                'active',
                'Deactivating freezes the computed consumption row as-is (human-owned again); reactivating recomputes immediately.',
              )}
            </FormGroup>
          )}

          <FormGroup label="Note" fieldId="spla-binding-note">
            <TextInput
              id="spla-binding-note"
              type="text"
              value={note}
              placeholder="Why this attribution"
              onChange={(_event, value) => {
                setNote(value);
                clearFieldError('note');
              }}
              validated={fieldErrors['note'] !== undefined || !isNoteValid ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper(
              'note',
              !isNoteValid
                ? `At most ${NOTE_MAX} characters.`
                : `Internal staff memo.${isEdit ? ' Clearing it removes the note.' : ''}`,
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="spla-binding-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaBindingModal;
